import type { BdcEdiFileType, BdcEdiShipment } from "../../../services/bdcEdi";

/** POST /bdc/edi-exports takes at most 100 shipments (BDC_EDI_MAX_SHIPMENTS on the server). */
export const BDC_EDI_MAX_SHIPMENTS = 100;

export const EDI_FILE_LABEL: Record<BdcEdiFileType, string> = {
  do: "DO (รายร้าน)",
  invoice: "Invoice (ต่อ PO) — ล็อกเป็นรอนำส่ง 7-11",
};

/** A row can be ticked when at least one file can take it now (the server's flags). */
export const isSelectable = (row: BdcEdiShipment): boolean => row.can_export_do || row.can_export_invoice;

/** Why a row offers no file, or what it still offers — the note beside it. */
export const rowNote = (row: BdcEdiShipment): string | null => {
  if (row.is_test_mode) return "สาขาโหมดทดสอบ (INV TB…) — ไม่มีไฟล์ส่ง 7-11";
  if (row.status === "queued_7_11") return "รอนำส่ง 7-11 แล้ว — download ซ้ำได้จากประวัติ";
  if (!row.can_export_do && !row.can_export_invoice) return "ยังสร้างไฟล์ไม่ได้";
  if (!row.can_export_invoice && row.status === "do_confirmed") return "ยังไม่ออก INV — ได้เฉพาะไฟล์ DO";
  if (!row.can_export_do && row.kind === "X") return "ไม่มีรายร้าน — ไม่มีส่วนในไฟล์ DO";
  return null;
};

/**
 * Why the chosen selection × files cannot be previewed, or null. Each ticked PO must fit at
 * least one chosen file (the server refuses the rest with error.po_numbers anyway).
 */
export const selectionProblem = (
  rows: BdcEdiShipment[],
  selected: ReadonlySet<string>,
  types: ReadonlySet<BdcEdiFileType>,
): string | null => {
  const picked = rows.filter((row) => selected.has(row.shipment_uuid));
  if (picked.length === 0) return "เลือก PO อย่างน้อย 1 รายการ";
  if (picked.length > BDC_EDI_MAX_SHIPMENTS) return `เลือกได้ครั้งละไม่เกิน ${BDC_EDI_MAX_SHIPMENTS} PO`;
  if (types.size === 0) return "เลือกไฟล์อย่างน้อย 1 ชนิด";
  const misfit = picked.filter(
    (row) => !((types.has("do") && row.can_export_do) || (types.has("invoice") && row.can_export_invoice)),
  );
  if (misfit.length > 0) return `PO ${misfit.map((r) => r.po_number).join(", ")} ใช้กับไฟล์ที่เลือกไม่ได้`;
  return null;
};

/** `B269005674–675` — first and last INV of a file, sharing the leading digits. */
export const invoiceRange = (from: string | null, to: string | null): string => {
  if (!from) return "";
  if (!to || to === from) return from;
  let common = 0;
  while (common < from.length && from[common] === to[common]) common += 1;
  // keep at least the last three digits of the second number readable
  return `${from}–${to.slice(Math.min(common, to.length - 3))}`;
};

export const formatBytes = (bytes: number): string =>
  bytes < 1024 ? `${bytes} B` : bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1024 / 1024).toFixed(2)} MB`;
